import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PractitionerRoleGroupsResponse } from '@/types/settings-api';

/**
 * Practitioner list GROUPED BY ROLE, for the Practitioner tab.
 *
 * `GET /api/v1/Practice/{organizationId}/PractitionerRoles/Grouped?showArchived={bool}`
 *
 * ## Why this is not the flat list plus a `groupBy`
 *
 * The flat read (`useDoctorsList`) returns 20 fields and **not one of them is a
 * role**, so the client has nothing to group on. The only role-adjacent field
 * on those rows is the speciality, and grouping by that files a Dietician and
 * an Immunopathologist under different "roles" — the role/speciality
 * conflation this work exists to undo. Hence a server-side grouping:
 * PracticeManagement owns which role each person holds, Identity owns the
 * display taxonomy, and Practice.Aggregator joins them.
 *
 * ## It returns MORE PEOPLE than the flat list
 *
 * Measured on dev 2026-09-07: 215 here against 153 flat. The extra 62 are
 * Nurses and Phlebotomists — practitioner professions stored in
 * `dbo.AddStaff`, which the flat doctor read never saw. The bucket-3 groups
 * sum to exactly the flat count of 153, which is how the two reconcile.
 *
 * Each row's `storageBucket` says which table it came from, and CALLERS MUST
 * BRANCH ON IT before building a link or firing a mutation — see
 * `GroupedPractitioner.storageBucket`.
 *
 * ## Ordering
 *
 * Groups arrive already ordered (category, then order within category, then
 * name). Deliberately not re-sorted here.
 */
export function useDoctorsListGrouped(
  organizationId: string | null | undefined,
  showArchived: boolean,
) {
  return useQuery({
    queryKey: ['settings', 'doctors-list-grouped', organizationId, showArchived],
    queryFn: async ({ signal }) => {
      if (!organizationId) {
        throw new Error('useDoctorsListGrouped called without orgId');
      }
      const res = await get<PractitionerRoleGroupsResponse>(
        `/api/v1/Practice/${organizationId}/PractitionerRoles/Grouped?showArchived=${showArchived}`,
        { signal },
      );
      // A `groups` array is what every consumer indexes; normalise the empty
      // case once here rather than guarding at each use.
      return {
        ...res,
        groups: res?.groups ?? [],
        totalNumberOfRecords: res?.totalNumberOfRecords ?? 0,
      } satisfies PractitionerRoleGroupsResponse;
    },
    enabled: !!organizationId,
  });
}
